// ====================================================================
//  EYEWALL — WATCHERS.EXE  (Ambulation Zone / The Annex)
//  A panel of MANY PHOTOREAL human eyes whose irises all track the
//  cursor at once. Built on the same idea as THE EYE: each eye renders a
//  real photographed iris (friendlyeye1.png / eyeiris.png / crasheye.png)
//  clipped to a disc that physically slides toward the pointer inside a
//  wet, shaded sclera framed by an almond eyelid opening with lashes.
//
//  We only have a few photoreal iris assets, so the wall reads as a VARIED
//  crowd of watchers — not identical clones — through per-eye framing:
//  different sizes (scale), slight rotations (rot), a gentle hsl tint
//  blended over the iris photo (tintHue/Sat/Light), four sclera warmth
//  variants and four almond lid shapes. The user explicitly wants the
//  FRIENDLY / kind photoreal eye (friendlyeye1.png) used, so roughly half
//  the wall is the friendly iris.
//
//  PERFORMANCE: zero canvases, zero per-eye timers. ONE shared document
//  mousemove listener (rafThrottle from the shared util) translates every
//  iris group toward the pointer via a CSS transform, clamped inside its
//  almond. The listener is removed (and the rAF wrapper cancelled) on
//  unmount. Blinks are CSS-only, staggered per eye. prefers-reduced-motion
//  holds a steady centered gaze with no blink and no tracking.
//
//  A file at src/pages/Components/EyeWall reaches images via ../../../images.
//
//  Mounts inside a WindowsCard with programText WATCHERS.EXE.
// ====================================================================

import React, { useEffect, useMemo, useRef } from 'react';
import { rafThrottle, prefersReducedMotion } from '../_shared/sketchFrame';
import friendlyUrl from '../../../images/friendlyeye1.png';
import irisUrl from '../../../images/eyeiris.png';
import crashUrl from '../../../images/crasheye.png';
import eyeData from './eyes.json';
import './eyewall.css';

// SVG geometry (viewBox 0 0 100 80). The iris photo is a disc of radius
// IRIS_R centered at (EYE_CX, EYE_CY); it may travel up to TRAVEL_X/Y from
// center before clamping, so the iris slides inside the opening and never
// leaves the almond.
const EYE_CX = 50;
const EYE_CY = 40;
const IRIS_R = 17;
const TRAVEL_X = 12; // max horizontal iris travel (svg units)
const TRAVEL_Y = 7;  // max vertical iris travel (svg units)

// Map the descriptor's src key to the imported photoreal asset.
const SRC_URLS = { friendly: friendlyUrl, iris: irisUrl, crash: crashUrl };

// Sclera warmth variants: [innerLight, mid, outerRim] stops for the shading
// radial gradient, giving each eye a slightly different eyeball tone.
const SCLERA_STOPS = {
    warm:  ['#fdf9f3', '#efe3d4', '#cbb8a2'],
    cool:  ['#f9fbfd', '#e6ecf1', '#bfc8d0'],
    pale:  ['#fefdfb', '#f1ece4', '#d6cabb'],
    ruddy: ['#fdf6f1', '#ecdcd2', '#c9aa9c']
};

// Almond opening shape variants. Each is a closed path on the 100x80 view,
// centered on (50,40). Different curvatures read as different eye shapes.
const LID_PATHS = {
    almond: 'M 12 40 C 28 20, 72 20, 88 40 C 72 60, 28 60, 12 40 Z',
    narrow: 'M 12 40 C 30 28, 70 28, 88 40 C 70 52, 30 52, 12 40 Z',
    wide:   'M 9 40 C 26 16, 74 16, 91 40 C 74 64, 26 64, 9 40 Z',
    round:  'M 16 40 C 28 22, 72 22, 84 40 C 72 58, 28 58, 16 40 Z'
};

// The crease line that hugs the top of each lid variant (cosmetic shadow).
const CREASE_PATHS = {
    almond: 'M 17 33 C 32 19, 68 19, 83 33',
    narrow: 'M 17 35 C 33 26, 67 26, 83 35',
    wide:   'M 14 33 C 30 16, 70 16, 86 33',
    round:  'M 20 33 C 32 21, 68 21, 80 33'
};

// One photoreal eye as inline SVG. `idx` namespaces the SVG defs (gradients
// and clip paths need unique ids per instance).
function Eye({
    idx, src, scale, rot, tintHue, tintSat, tintLight,
    sclera, lid, blinkDelay, blinkDur, reduced
}) {
    const scleraGrad = `ew-sclera-${idx}`;
    const wetGrad = `ew-wet-${idx}`;
    const lidClip = `ew-lid-${idx}`;
    const irisClip = `ew-iris-clip-${idx}`;

    const href = SRC_URLS[src] || friendlyUrl;
    const stops = SCLERA_STOPS[sclera] || SCLERA_STOPS.warm;
    const lidPath = LID_PATHS[lid] || LID_PATHS.almond;
    const creasePath = CREASE_PATHS[lid] || CREASE_PATHS.almond;
    const tint = `hsl(${tintHue} ${tintSat}% ${tintLight}%)`;

    const lidStyle = reduced
        ? undefined
        : { animationDuration: `${blinkDur}s`, animationDelay: `${blinkDelay}s` };

    return (
        <svg
            className="eyewall-eye"
            viewBox="0 0 100 80"
            style={{ '--eye-scale': scale, '--eye-rot': `${rot}deg` }}
            aria-hidden="true"
        >
            <defs>
                {/* sclera: soft wet shading, brighter toward top-left light */}
                <radialGradient id={scleraGrad} cx="42%" cy="34%" r="74%">
                    <stop offset="0%" stopColor={stops[0]} />
                    <stop offset="62%" stopColor={stops[1]} />
                    <stop offset="100%" stopColor={stops[2]} />
                </radialGradient>

                {/* wet glassy dome highlight over the whole eyeball */}
                <radialGradient id={wetGrad} cx="42%" cy="30%" r="70%">
                    <stop offset="0%" stopColor="#ffffff" stopOpacity="0.40" />
                    <stop offset="34%" stopColor="#ffffff" stopOpacity="0.05" />
                    <stop offset="100%" stopColor="#000000" stopOpacity="0.20" />
                </radialGradient>

                {/* almond opening — clips the wet eyeball to the lids */}
                <clipPath id={lidClip}>
                    <path d={lidPath} />
                </clipPath>

                {/* circular clip so the rectangular iris photo reads as a disc */}
                <clipPath id={irisClip}>
                    <circle cx={EYE_CX} cy={EYE_CY} r={IRIS_R} />
                </clipPath>
            </defs>

            {/* ---- eyeball contents, clipped to the almond ---- */}
            <g clipPath={`url(#${lidClip})`}>
                {/* shaded sclera base */}
                <rect x="0" y="0" width="100" height="80" fill={`url(#${scleraGrad})`} />

                {/* warm caruncle hint + inner-corner curvature shadow */}
                <ellipse cx="50" cy="26" rx="40" ry="14" fill="#7a6b60" opacity="0.10" />
                <ellipse cx="15" cy="42" rx="5" ry="3.4" fill="#d49696" opacity="0.45" />

                {/* faint branching red veins from the corners */}
                <g stroke="#be3834" strokeWidth="0.5" fill="none" opacity="0.5" strokeLinecap="round">
                    <path d="M 14 38 C 22 36, 26 39, 32 37 M 28 38 C 30 40, 33 39, 36 41" />
                    <path d="M 86 44 C 78 45, 74 42, 69 44" />
                </g>

                {/* ---- iris : the PHOTO, cursor-tracked ---- */}
                {/* This group is translated by the shared listener toward the
                    pointer. The photo is clipped to a disc; a contact shadow,
                    tint, limbal ring and catchlight sell it as a wet eyeball. */}
                <g className="eyewall-iris" data-eye>
                    {/* contact shadow under the iris dome */}
                    <ellipse cx={EYE_CX} cy={EYE_CY + 1} rx={IRIS_R + 1.2} ry={IRIS_R + 1.2}
                             fill="#000000" opacity="0.22" />

                    {/* the real photographed iris, masked to a disc */}
                    <image
                        href={href}
                        xlinkHref={href}
                        x={EYE_CX - IRIS_R}
                        y={EYE_CY - IRIS_R}
                        width={IRIS_R * 2}
                        height={IRIS_R * 2}
                        clipPath={`url(#${irisClip})`}
                        preserveAspectRatio="xMidYMid slice"
                    />

                    {/* gentle per-eye color tint over the iris for variety */}
                    <circle cx={EYE_CX} cy={EYE_CY} r={IRIS_R}
                            clipPath={`url(#${irisClip})`}
                            fill={tint} opacity="0.16" style={{ mixBlendMode: 'color' }} />

                    {/* dark limbal ring crisps the iris rim */}
                    <circle cx={EYE_CX} cy={EYE_CY} r={IRIS_R - 0.4}
                            fill="none" stroke="#161013" strokeWidth="0.9" opacity="0.7" />

                    {/* crisp specular catchlight (travels with the iris) */}
                    <ellipse cx={EYE_CX - 5} cy={EYE_CY - 6} rx="2.1" ry="2.6"
                             fill="#ffffff" opacity="0.9" />
                    <circle cx={EYE_CX + 4} cy={EYE_CY + 5} r="0.9"
                            fill="#ffffff" opacity="0.45" />
                </g>

                {/* top crease shadow over the eyeball (fixed to the lid) */}
                <rect x="0" y="0" width="100" height="34" fill="#2a1d16" opacity="0.16" />
                {/* wet glassy dome highlight over everything */}
                <rect x="0" y="0" width="100" height="80" fill={`url(#${wetGrad})`} />
            </g>

            {/* ---- upper-lid blink mask : sweeps down via CSS ---- */}
            <g clipPath={`url(#${lidClip})`}>
                <rect
                    className="eyewall-lid"
                    x="6" y="16" width="88" height="26"
                    fill="#1d1714"
                    style={lidStyle}
                />
            </g>

            {/* ---- almond rim ink outline (upper + lower lid arcs) ---- */}
            <path d={lidPath} fill="none" stroke="#120d0b" strokeWidth="1.4" />
            {/* soft crease shadow above the upper lid */}
            <path d={creasePath} fill="none" stroke="#3a2a24" strokeWidth="2" opacity="0.4" />

            {/* a few upper lashes, curling up and out */}
            <g stroke="#0e0907" strokeWidth="1" fill="none" strokeLinecap="round">
                <path d="M 30 24 q -2 -4, -4 -6" />
                <path d="M 42 21 q -1 -4, -2 -7" />
                <path d="M 58 21 q 1 -4, 2 -7" />
                <path d="M 70 24 q 2 -4, 4 -6" />
            </g>
            {/* one lower lash hint */}
            <path d="M 50 58 l 0 5" stroke="#0e0907" strokeWidth="0.7" />
        </svg>
    );
}

function EyeWall() {
    const wrapRef = useRef(null);
    const reduced = useMemo(() => prefersReducedMotion(), []);

    useEffect(() => {
        const root = wrapRef.current;
        if (!root) return undefined;
        // Steady, centered gaze under reduced motion: no tracking listener.
        if (reduced) return undefined;

        // Collect every iris group ONCE; the listener just retargets them.
        const irises = Array.prototype.slice.call(
            root.querySelectorAll('.eyewall-iris')
        );
        if (!irises.length) return undefined;

        // Single shared, rAF-coalesced pointer handler. Each iris translates
        // toward the cursor/finger, normalised by ITS OWN on-screen position so
        // all eyes converge on the pointer, then clamped inside the almond.
        const onMove = rafThrottle((e) => {
            // Read the position from a mouse/pointer event or a touch event, so
            // the whole wall follows a finger drag on mobile just like a cursor.
            let px;
            let py;
            if (e.touches && e.touches.length) {
                px = e.touches[0].clientX;
                py = e.touches[0].clientY;
            } else if (e.changedTouches && e.changedTouches.length) {
                px = e.changedTouches[0].clientX;
                py = e.changedTouches[0].clientY;
            } else {
                px = e.clientX;
                py = e.clientY;
            }
            if (typeof px !== 'number' || typeof py !== 'number') return;
            for (let i = 0; i < irises.length; i++) {
                const el = irises[i];
                const r = el.getBoundingClientRect();
                const cx = r.left + r.width / 2;
                const cy = r.top + r.height / 2;
                // direction to pointer, in -1..1 of a comfortable reach radius
                const dx = (px - cx) / 90;
                const dy = (py - cy) / 90;
                const len = Math.hypot(dx, dy) || 1;
                // clamp magnitude to 1 so far-away pointers don't overshoot
                const m = Math.min(len, 1) / len;
                // svg user units; translate in px (svg overflow visible)
                const ux = Math.max(-1, Math.min(1, dx * m)) * TRAVEL_X;
                const uy = Math.max(-1, Math.min(1, dy * m)) * TRAVEL_Y;
                el.style.transform = `translate(${ux.toFixed(2)}px, ${uy.toFixed(2)}px)`;
            }
        });

        // Prefer Pointer Events (mouse + touch + pen in one stream) where
        // available; otherwise listen to mouse + touch so the gaze still
        // tracks a finger on older mobile browsers. All passive — we never
        // block scrolling, the eyes just follow whatever the finger does.
        const usePointer = typeof window !== 'undefined' && typeof window.PointerEvent !== 'undefined';
        if (usePointer) {
            document.addEventListener('pointermove', onMove, { passive: true });
            document.addEventListener('pointerdown', onMove, { passive: true });
        } else {
            document.addEventListener('mousemove', onMove, { passive: true });
            document.addEventListener('touchmove', onMove, { passive: true });
            document.addEventListener('touchstart', onMove, { passive: true });
        }
        return () => {
            document.removeEventListener('pointermove', onMove);
            document.removeEventListener('pointerdown', onMove);
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('touchmove', onMove);
            document.removeEventListener('touchstart', onMove);
            if (onMove && onMove.cancel) onMove.cancel();
        };
    }, [reduced]);

    const eyes = eyeData.eyes || [];

    return (
        <div ref={wrapRef} className="eyewall-wrap">
            <div className="eyewall-grid">
                {eyes.map((e, i) => (
                    <Eye
                        key={i}
                        idx={i}
                        src={e.src}
                        scale={e.scale}
                        rot={e.rot}
                        tintHue={e.tintHue}
                        tintSat={e.tintSat}
                        tintLight={e.tintLight}
                        sclera={e.sclera}
                        lid={e.lid}
                        blinkDelay={e.blinkDelay}
                        blinkDur={e.blinkDur}
                        reduced={reduced}
                    />
                ))}
            </div>
        </div>
    );
}

export default EyeWall;
